import { useMutation, useQuery, useQueryClient } from 'react-query';
import templatesApi from '../../../api/templates.api';
import { EventTemplateCreateEdit, EventTemplateFormValues } from '../../../types';
import { useToastProvider } from '@qualio/ui-components';
import { AxiosError } from 'axios';
import { convertResponseToTemplateFormValues } from '../../../utils/formTemplateUtils';
import { extractMessageFromError, isNotFoundError } from '../../../utils/errorUtils';
import { baseEventTemplateQueryKey } from '../../../hooks';
import { DefaultErrorMessage } from '../../../displayStrings';

export const useTemplateQuery = (companyId: number, eventTemplateId: string | undefined) => {
  const { showToast } = useToastProvider();

  return useQuery(
    [baseEventTemplateQueryKey, companyId, eventTemplateId],
    async () => {
      const data = await templatesApi.getEventTemplateDetails(companyId, Number(eventTemplateId));
      return {
        eventTemplateFormValues: convertResponseToTemplateFormValues(data),
        defaultOwnerHasPermission: data.default_owner_has_permission,
        ownerId: data.owner_id,
        ownerName: data.owner,
      };
    },
    {
      // A missing template is a terminal outcome: don't retry it (react-query defaults to 3),
      // and let the view render the not-found page instead of firing a generic error toast.
      retry: (failureCount, error) => !isNotFoundError(error) && failureCount < 3,
      onError: (error: AxiosError) => {
        if (isNotFoundError(error)) {
          return;
        }
        showToast({
          title: 'Failed to fetch event template details',
          description: DefaultErrorMessage,
          status: 'error',
        });
      },
      onSuccess(data) {
        if (data.eventTemplateFormValues.steps.length) {
          data.eventTemplateFormValues.steps.sort((step1, step2) => {
            return step1.order - step2.order;
          });
        }
      },
    },
  );
};

type UpdateArg = {
  companyId: number;
  templateId: number;
  originalTemplate: EventTemplateFormValues;
};

const generateUpdateFn = ({ companyId, templateId, originalTemplate }: UpdateArg) => {
  return async (payload: EventTemplateFormValues) => {
    const { steps, fields, default_owner_id, ...restOfKeys } = payload;
    const fieldsArray = Object.values(fields).map((field) => {
      const originalField = originalTemplate.fields[field.attribute_name];
      const baseObject = originalField ?? {};

      return {
        ...baseObject,
        ...field,
      };
    });

    const updateObject: EventTemplateCreateEdit = {
      id: templateId,
      steps,
      fields: fieldsArray,
      ...restOfKeys,
    };

    updateObject.default_owner_id = default_owner_id ? Number(default_owner_id) : undefined;

    const data = await templatesApi.updateTemplate(companyId, `${templateId}`, updateObject, fieldsArray, steps);
    return convertResponseToTemplateFormValues(data);
  };
};

export const useTemplateMutation = (args: UpdateArg) => {
  const { showToast } = useToastProvider();
  const queryClient = useQueryClient();
  const mutationFn = generateUpdateFn(args);

  const mutationCallbacks = {
    onSuccess: (data: EventTemplateFormValues) => {
      queryClient.invalidateQueries([baseEventTemplateQueryKey, args.companyId, `${args.templateId}`]).then(() => {
        showToast({
          title: 'Event template updated',
          description: `${data.name} was successfully updated`,
          status: 'success',
          'data-cy': 'qe-event-template-update-success-toast',
        });
      });
    },
    onError: (error: AxiosError) => {
      showToast({
        title: 'Event template not updated',
        description: extractMessageFromError(error),
        status: 'error',
      });
    },
  };

  return useMutation(mutationFn, mutationCallbacks);
};
